import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ** MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

// ** Components
import Editor from '@erikdakoda/editor-ui/Editor';
import ImageUploadControl from '@erikdakoda/cloudinary-ui/components/ImageUploadControl';

// ** Hooks
import { useFeedbackStore } from '../hooks/useFeedbackStore';
import { useConfirm } from '@erikdakoda/confirm-ui/useConfirm';

// ** Form
import type { FieldValues, UseFormReturn } from 'react-hook-form';

// ** Schemas
import {
  useDeleteFeedback,
  useFindUniqueFeedback,
  useUpdateFeedback,
} from '@erikdakoda/database/hooks/feedback';
import { modelSchema, modelUpdateSchema } from '@erikdakoda/database/zodSchemas';

// ** Types
import { FeedbackForEditSelect } from '../types/feedback';

// ** Utils
import type { FieldGroup } from '@erikdakoda/editor-ui/types';
import { useSnack } from '@erikdakoda/ui/hooks/useSnack';
import { useException } from '@erikdakoda/ui/hooks/useException';

const FeedbackUpdateSchema = modelUpdateSchema('Feedback');
const FeedbackSchema = modelSchema('Feedback');

export type FeedbackEditorProps = {
  feedbackId?: string;
  setEditedFeedbackId: (feedbackId: string | null) => void;
};

export default function FeedbackEditor({ feedbackId, setEditedFeedbackId }: FeedbackEditorProps) {
  'use memo';

  const { t } = useTranslation('feedback-ui');
  const formRef = useRef<UseFormReturn<FieldValues> | null>(null);
  const updateFeedback = useUpdateFeedback().mutateAsync;
  const deleteFeedback = useDeleteFeedback().mutateAsync;
  const editedFeedbackId = useFeedbackStore((state) => state.editedFeedbackId);
  const setCurrentFeedbackId = useFeedbackStore((state) => state.setCurrentFeedbackId);
  const confirm = useConfirm();
  const { snack } = useSnack();
  const exception = useException();
  const [pendingAction, setPendingAction] = useState<'close' | 'discard' | 'submit' | null>(null);

  if (editedFeedbackId) {
    feedbackId = editedFeedbackId;
  }

  const { data: feedback, isLoading: isLoadingFeedback } = useFindUniqueFeedback(
    {
      where: { id: feedbackId ?? '' },
      select: FeedbackForEditSelect,
    },
    { enabled: !!feedbackId },
  );

  if (feedbackId && !isLoadingFeedback && !feedback) {
    setEditedFeedbackId(null);
  }

  // keep: stable reference for third-party API
  const handleCloseFeedback = useCallback(async () => {
    setPendingAction('close');
    try {
      await updateFeedback({
        where: { id: feedbackId },
        data: { closedAt: feedback?.closedAt ? null : new Date() },
      });
      if (!feedback?.closedAt) {
        setEditedFeedbackId(null);
      }
      snack.success(feedback?.closedAt ? 'Feedback reopened' : 'Feedback closed');
    } catch (err: unknown) {
      exception(err);
    } finally {
      setPendingAction(null);
    }
  }, [setEditedFeedbackId, feedbackId, feedback?.closedAt, updateFeedback, snack, exception]);

  // keep: stable reference for third-party API
  const handleCloseDialog = useCallback(async () => {
    setEditedFeedbackId(null);
  }, [setEditedFeedbackId]);

  // keep: stable reference for third-party API
  const handleDiscard = useCallback(async () => {
    const { confirmed } = await confirm({
      title: t('discardFeedbackTitle'),
      description: t('discardFeedbackDescription'),
    });
    if (!confirmed) return;

    setPendingAction('discard');
    try {
      await deleteFeedback({ where: { id: feedbackId } });
      setCurrentFeedbackId(null);
      setEditedFeedbackId(null);
      snack.success(t('feedbackDiscarded'));
    } catch (err: unknown) {
      exception(err);
    } finally {
      setPendingAction(null);
    }
  }, [
    confirm,
    setCurrentFeedbackId,
    setEditedFeedbackId,
    deleteFeedback,
    feedbackId,
    snack,
    exception,
    t,
  ]);

  // keep: stable reference for third-party API
  const handleSubmit = useCallback(async () => {
    const form = formRef.current;
    const values = form?.getValues() ?? {};
    form?.clearErrors();

    const trimmedTitle = (values.title ?? feedback?.title ?? '').toString().trim();
    if (!trimmedTitle) {
      form?.setError('title', { type: 'manual', message: t('pleaseEnterATitle') });
      if (!form) {
        snack.error(t('pleaseEnterATitle'));
      }
      return;
    }

    setPendingAction('submit');
    try {
      await updateFeedback({ where: { id: feedbackId }, data: { submittedAt: new Date() } });
      setCurrentFeedbackId(null);
      setEditedFeedbackId(null);
      snack.success(t('feedbackSubmitted'));
    } catch (err: unknown) {
      exception(err);
    } finally {
      setPendingAction(null);
    }
  }, [
    feedback?.title,
    setCurrentFeedbackId,
    setEditedFeedbackId,
    updateFeedback,
    feedbackId,
    snack,
    exception,
    t,
  ]);

  // keep: stable reference for third-party API
  const actionsElement = useMemo(() => {
    return (
      <Box className="footer-actions" sx={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
        {feedback?.submittedAt ? (
          <Button
            size="large"
            variant="contained"
            onClick={handleCloseFeedback}
            loading={pendingAction === 'close'}
          >
            {feedback.closedAt ? 'Reopen' : 'Close'}
          </Button>
        ) : (
          <>
            <Button
              size="large"
              onClick={handleDiscard}
              loading={pendingAction === 'discard'}
              disabled={pendingAction !== null}
            >
              Discard
            </Button>
            <Button
              size="large"
              variant="contained"
              onClick={handleSubmit}
              loading={pendingAction === 'submit'}
              disabled={pendingAction !== null}
            >
              Submit
            </Button>
          </>
        )}
      </Box>
    );
  }, [handleDiscard, handleSubmit, pendingAction]);

  // keep: stable reference for third-party API
  const footerElement = useMemo(() => {
    return (
      <Typography
        variant="body2"
        sx={{
          width: '100%',
          textAlign: 'center',
        }}
      >
        {t('feedbackEditorFooterYouCan')}{' '}
        <Link href="#" onClick={handleCloseDialog}>
          {t('feedbackEditorFooterCloseThisBox')}
        </Link>{' '}
        {t('feedbackEditorFooterWithoutSubmitting')}
      </Typography>
    );
  }, [handleCloseDialog, t]);

  if (feedbackId && !isLoadingFeedback && !feedback) {
    setCurrentFeedbackId(null);
    setEditedFeedbackId(null);
  }

  if (!feedback) return null;

  const feedbackTypeOptions = ['question', 'feedback', 'bug', 'request'].map((type) => ({
    id: type,
    label: type.charAt(0).toUpperCase() + type.slice(1),
  }));

  const spec = {
    type: {
      options: feedbackTypeOptions,
      columns: 3,
      props: {
        sx: { mx: 'auto', width: 'calc((100% - 40px) / 3)' },
      },
      disabled: !!feedback.submittedAt,
      undimmed: !!feedback.submittedAt,
    },
    title: {
      disabled: !!feedback.submittedAt,
      undimmed: !!feedback.submittedAt,
    },
    description: {
      rows: feedback.submittedAt ? 1 : 5,
      disabled: !!feedback.submittedAt,
      undimmed: !!feedback.submittedAt,
    },
    pageUrl: {
      disabled: !!feedback.submittedAt,
      undimmed: !!feedback.submittedAt,
    },
    stepsToReproduce: {
      rows: 5,
      helperText: 'Please describe the steps you took for this bug to occur.',
      invisible: (type) => type !== 'bug',
      watchField: 'type',
      disabled: !!feedback.submittedAt,
      undimmed: !!feedback.submittedAt,
    },
    notes: {
      label: 'Dev team notes',
      rows: 5,
      invisible: (submittedAt) => !submittedAt,
      watchField: 'submittedAt',
    },
    screenshotIds: {
      component: ImageUploadControl,
      helperText: 'Upload screenshots to illustrate your feedback',
      props: {
        uploadPreset: 'Feedback',
        cropping: false,
        multiple: true,
        deleteEndpoint: '/api/delete-feedback-screenshot',
      },
      disabled: !!feedback.submittedAt,
    },
  };

  const fields: (string | FieldGroup)[] = [
    'type',
    'title',
    'description',
    'pageUrl',
    'stepsToReproduce',
    'notes',
    'screenshotIds',
  ];

  return (
    <Editor
      className="feedback-editor"
      item={feedback}
      typeName="Feedback"
      schema={FeedbackUpdateSchema}
      displaySchema={FeedbackSchema}
      dialog={true}
      title={feedback.title?.trim() ? feedback.title : 'Submit Feedback'}
      innerWidth="sm"
      autoSave={true}
      spec={spec}
      fields={fields}
      actionsElement={actionsElement}
      footElement={feedback.submittedAt ? undefined : footerElement}
      formRef={formRef}
      // @ts-expect-error
      updateItem={updateFeedback}
      onCancel={handleCloseDialog}
      scrollTrackOutside
    />
  );
}
